import React, { createContext, useContext, useState, useEffect } from 'react';
import jwt_decode from 'jwt-decode';
import axios from 'axios';
import { getCurrentOrganization, getOrganizationId } from '../utils/organizationContext';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [organizationSlug, setOrganizationSlug] = useState(null);
  const [organizationId, setOrganizationId] = useState(null);

  // Check if token is expired
  const isTokenExpired = (token) => {
    try {
      const decodedToken = jwt_decode(token);
      const currentTime = Date.now() / 1000;
      return decodedToken.exp < currentTime;
    } catch (error) {
      return true;
    }
  };

  // Handle unauthorized responses globally
  const handleUnauthorized = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);
    window.location.href = '/login';
  };

  // Wrap fetch calls to handle auth
  const authenticatedFetch = async (url, options = {}) => {
    const token = localStorage.getItem('token');
    
    if (token && isTokenExpired(token)) {
      handleUnauthorized();
      throw new Error('Session expired');
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`
      }
    });

    if (response.status === 401) {
      handleUnauthorized();
      throw new Error('Unauthorized');
    }

    return response;
  };

  // Load organization from URL or user context
  useEffect(() => {
    const loadOrganization = async () => {
      // Get organization slug from URL
      const slug = getCurrentOrganization();
      setOrganizationSlug(slug);
      
      // If authenticated, use organization_id from user/token
      const token = localStorage.getItem('token');
      if (token && !isTokenExpired(token)) {
        try {
          const userData = JSON.parse(localStorage.getItem('user'));
          if (userData?.organization_id) {
            setOrganizationId(userData.organization_id);
            // Also update slug if we have org info
            if (userData.organization_slug) {
              setOrganizationSlug(userData.organization_slug);
            }
            return;
          }
        } catch (error) {
          console.error('Error parsing user data:', error);
        }
      }
      
      // If not authenticated, get org ID from slug
      if (slug) {
        const orgId = await getOrganizationId(slug);
        if (orgId) {
          setOrganizationId(orgId);
        }
      }
    };
    
    loadOrganization();
  }, []);

  useEffect(() => {
    console.log('AuthContext: Starting authentication check...');
    const token = localStorage.getItem('token');
    console.log('AuthContext: Token found:', !!token);
    
    if (token && !isTokenExpired(token)) {
      console.log('AuthContext: Valid token, setting authenticated...');
      setIsAuthenticated(true);
      try {
        const userData = JSON.parse(localStorage.getItem('user'));
        console.log('AuthContext: User data loaded:', userData?.role);
        setUser(userData);
        
        // Update organization from user data
        if (userData?.organization_id) {
          setOrganizationId(userData.organization_id);
        }
        if (userData?.organization_slug) {
          setOrganizationSlug(userData.organization_slug);
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    } else if (token) {
      console.log('AuthContext: Token expired, handling unauthorized...');
      handleUnauthorized();
    } else {
      console.log('AuthContext: No token found');
      // Load organization from URL for public routes
      const slug = getCurrentOrganization();
      setOrganizationSlug(slug);
    }
    
    console.log('AuthContext: Setting loading to false');
    setIsLoading(false);
  }, []);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setIsAuthenticated(true);
    setUser(userData);
    
    // Update organization from user data
    if (userData?.organization_id) {
      setOrganizationId(userData.organization_id);
    }
    if (userData?.organization_slug) {
      setOrganizationSlug(userData.organization_slug);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      setIsAuthenticated,
      user,
      isLoading,
      login, 
      logout,
      authenticatedFetch,
      organizationSlug,
      organizationId,
      setOrganizationSlug,
      setOrganizationId
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext); 